import React, {
  createContext,
  useCallback,
  useContext,
  useEffect, useId,
  useMemo,
  useState,
} from 'react';

export enum GameType {
  SUM_OF_POINTS = 'SUM_OF_POINTS',
  DARTS = 'DARTS',
}

interface GameSettings {
  totalRounds: number;
  doublePointsStatus: boolean;
  audioStatus: boolean;
  type: GameType;
  target: number | undefined;
  gameId: string;
}

export const DARTS_TARGET = {
  LOW: 31,
  MEDIUM: 51,
  HIGH: 61,
}

const DEFAULT_GAME_SETTINGS: GameSettings = {
  totalRounds: 8,
  doublePointsStatus: true,
  audioStatus: true,
  type: GameType.SUM_OF_POINTS,
  target: undefined,
  gameId: "",
};

const getInitialGameSettings = (gameId: string): GameSettings => {
  const stored = localStorage.getItem('gameSettings');

  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      return {
        ...DEFAULT_GAME_SETTINGS,
        gameId,
        audioStatus:
          typeof parsed.audioStatus === 'boolean'
            ? parsed.audioStatus
            : DEFAULT_GAME_SETTINGS.audioStatus,
      };
    } catch {
      // If parsing fails, return default
      return DEFAULT_GAME_SETTINGS;
    }
  }

  return DEFAULT_GAME_SETTINGS;
};

interface GameContextType {
  isGameStarted: boolean;
  isGameFinished: boolean;
  currentTurn: number;
  currentRound: number;
  gameSettings: GameSettings;
  isLastRound: boolean;
  isFinalRound: boolean;
  isLastPlayer: boolean;
  startGame: () => void;
  handleBackToGame: () => void;
  resetGame: () => void;
  nextTurn: (value: number) => void;
  undoTurn: (turn: number, round: number) => void;
  updateSettings: (newSettings: Partial<GameSettings>) => void;
  setIsGameFinished: (isGameFinished: boolean) => void;
  setIsFinalRound: (value: boolean) => void;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

export const GameProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const gameId = useId();
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [isGameFinished, setIsGameFinished] = useState(false);
  const [isFinalRound, setIsFinalRound] = useState<boolean>(false);
  const [currentTurn, setCurrentTurn] = useState<number>(0);
  const [currentRound, setCurrentRound] = useState<number>(0);
  const [gameSettings, setGameSettings] = useState<GameSettings>(
    getInitialGameSettings(gameId)
  );

  const isLastRound = useMemo(
    () => currentRound + 1 === gameSettings.totalRounds,
    [currentRound, gameSettings.totalRounds]
  );

  const isLastPlayer = useMemo(
    () => currentTurn + 1 === gameSettings.totalRounds,
    [currentTurn, gameSettings.totalRounds]
  );

  const updateSettings = (newSettings: Partial<GameSettings>) => {
    setGameSettings((prev) => {
      const updatedSettings = { ...prev, ...newSettings };
      localStorage.setItem('gameSettings', JSON.stringify(updatedSettings));
      return updatedSettings;
    });
  };

  const startGame = () => {
    setIsGameStarted(true);
  };

  const handleBackToGame = () => {
    setIsGameFinished(false);
    setCurrentRound((prevState) => prevState - 1);
    setCurrentTurn((prevState) => prevState - 1);
  };

  const resetGame = () => {
    setIsGameStarted(false);
    setIsGameFinished(false);
    setIsFinalRound(false);
    setCurrentTurn(0);
    setCurrentRound(0);
    setGameSettings(getInitialGameSettings(""));
    sessionStorage.clear();
  };

  const nextTurn = (playersLength: number) => {
    if (currentTurn + 1 === playersLength) {
      setCurrentTurn(0);
      setCurrentRound((prev) => prev + 1);
    } else {
      setCurrentTurn((prev) => prev + 1);
    }

    if (
      currentRound + 1 === gameSettings.totalRounds &&
      currentTurn + 1 === playersLength
    ) {
      setIsGameFinished(true);
    }
  };

  const undoTurn = useCallback(
    (turn: number, round: number) => {
      setCurrentTurn(turn);
      setCurrentRound(round);
    },
    [setCurrentTurn, setCurrentRound]
  );

  useEffect(() => {
    const savedSettings = localStorage.getItem('gameSettings');
    const savedGameState = sessionStorage.getItem('gameState');

    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setGameSettings((prev) => ({
          ...prev,
          audioStatus:
            typeof parsed.audioStatus === 'boolean'
              ? parsed.audioStatus
              : prev.audioStatus,
        }));
      } catch {
        // ignore invalid localStorage
      }
    }

    if (savedGameState) {
      const { isStarted, currentTurn, currentRound } =
        JSON.parse(savedGameState);
      setIsGameStarted(isStarted);
      setCurrentTurn(currentTurn);
      setCurrentRound(currentRound);
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem(
      'gameState',
      JSON.stringify({
        isStarted: isGameStarted,
        currentTurn,
        currentRound,
      })
    );
  }, [isGameStarted, currentTurn, currentRound]);

  return (
    <GameContext.Provider
      value={{
        isGameStarted,
        isGameFinished,
        isFinalRound,
        currentTurn,
        currentRound,
        gameSettings,
        isLastRound: isLastRound && isGameStarted,
        isLastPlayer,
        handleBackToGame,
        startGame,
        resetGame,
        nextTurn,
        undoTurn,
        updateSettings,
        setIsGameFinished,
        setIsFinalRound,
      }}
    >
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within GameProvider');
  }
  return context;
};
